// frontend/packages/business-console/src/sentry.js
//
// Sentry browser SDK init for the business console. Imported at the very
// top of src/index.js so window.onerror / unhandledrejection handlers are
// installed before any other code runs.
//
// Config-driven: when REACT_APP_SENTRY_DSN is empty (default), Sentry.init()
// is not called and zero events are sent. CRA requires the REACT_APP_ prefix
// for any env var exposed to the browser bundle.

import React from "react";
import * as Sentry from "@sentry/react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";

const PII_KEYS = new Set([
  "name",
  "firstname",
  "lastname",
  "fullname",
  "email",
  "phone",
  "phonenumber",
  "mobile",
  "address",
  "addr",
  "line1",
  "line2",
  "street",
  "message",
  "notes",
  "note",
]);
const PII_PATTERN = /^(pii|ssn|dob|comment|password|secret|token|apikey|api_key|auth)/i;

// IMPORTANT: scrub() must NEVER mutate its input. The Sentry browser SDK's
// fetch breadcrumb integration passes a reference to the live RequestInit
// body into beforeBreadcrumb — mutating it would corrupt the outbound
// request, causing form submissions to arrive at the backend with
// `name`/`email`/`phone`/etc. literally set to "[scrubbed]". Always build
// and return a fresh object/array.
function scrub(value, depth = 0) {
  if (depth > 6 || value == null) return value;
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  // Don't try to walk non-plain objects (Date, FormData, URL, RegExp, etc.) —
  // return them as-is. Walking them would either crash or strip semantics.
  if (value.constructor && value.constructor !== Object) return value;
  const out = {};
  for (const key of Object.keys(value)) {
    const lower = key.toLowerCase();
    if (PII_KEYS.has(lower) || PII_PATTERN.test(lower)) {
      out[key] = "[scrubbed]";
    } else {
      out[key] = scrub(value[key], depth + 1);
    }
  }
  return out;
}

export function initSentry() {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "production",
    release: process.env.REACT_APP_SENTRY_RELEASE,

    integrations: [Sentry.browserTracingIntegration()],

    // 100% errors; 10% perf traces. The business console is operator-facing
    // and lower-traffic than the marketing site, but tracing every page nav
    // would still drown the project's quota.
    tracesSampleRate: 0.1,

    sendDefaultPii: false,

    // Tag every event so this app's signal is distinguishable from the
    // marketing-site events in the shared upryzze-frontends project.
    initialScope: {
      tags: { app: "business-console" },
    },

    beforeSend(event) {
      if (!event) return event;
      let next = event;
      if (next.request?.data || next.request?.headers || next.request?.cookies) {
        next = {
          ...next,
          request: {
            ...next.request,
            ...(next.request.data !== undefined
              ? { data: scrub(next.request.data) }
              : {}),
            ...(next.request.headers !== undefined
              ? { headers: scrub(next.request.headers) }
              : {}),
            ...(next.request.cookies !== undefined
              ? { cookies: scrub(next.request.cookies) }
              : {}),
          },
        };
      }
      if (next.user) next = { ...next, user: scrub(next.user) };
      if (next.contexts) next = { ...next, contexts: scrub(next.contexts) };
      if (next.extra) next = { ...next, extra: scrub(next.extra) };
      return next;
    },

    beforeBreadcrumb(breadcrumb) {
      if (!breadcrumb) return breadcrumb;
      let next = breadcrumb;
      if (next.data) next = { ...next, data: scrub(next.data) };
      if (next.message && typeof next.message === "string") {
        next = {
          ...next,
          message: next.message.replace(
            /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
            "[email-scrubbed]",
          ),
        };
      }
      return next;
    },
  });
}

export const sentryEnabled = !!SENTRY_DSN;

// Lightweight fallback. Intentionally text-only because the user's React
// tree is already broken at this point; we can't trust MUI/theme providers
// to render correctly.
function DefaultFallback() {
  return React.createElement("div", null, "Something went wrong. Our team has been notified.");
}

export function ErrorBoundary({ children, fallback }) {
  return React.createElement(
    Sentry.ErrorBoundary,
    { fallback: fallback || React.createElement(DefaultFallback) },
    children,
  );
}

export { Sentry };
